import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { ShoppingCart, CreditCard, Package, TrendingUp, Truck, ShieldCheck, Smartphone, Gift, Settings } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/services/solutions/ecommerce.css';

const EcommerceSolutions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  return (
    <div className="ecommerce-solutions-page">
      {/* Hero Section */}
      <motion.div 
        className="hero-section2 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Container>
          <Row className="align-items-center min-vh-80">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                <Badge bg="light" text="dark" className="mb-3 px-3 py-2">Next-Gen E-Commerce</Badge>
                <h1 className="display-3 fw-bold mb-4">End-to-End E-Commerce Solutions</h1>
                <p className="lead mb-4">Powerful, flexible platforms with seamless payment integration and inventory management to grow your online business.</p>
                <Button variant="light" size="lg" className="me-3 mb-3">Get Started</Button>
                <Button variant="outline-light" size="lg" className="mb-3">View Demo Store</Button>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="device-mockup"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <img 
                  src="/api/placeholder/600/400" 
                  alt="E-commerce Platform Preview" 
                  className="img-fluid rounded shadow-lg"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      {/* Platforms Section */}
      <section className="platforms-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our E-Commerce Platforms</h2>
            <p className="lead text-muted">Tailor-made solutions for businesses of all sizes</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { 
                  title: "Essential Store", 
                  description: "Perfect for small businesses just starting their e-commerce journey",
                  features: ["Up to 500 products", "Standard payment gateways", "Basic inventory tracking", "Mobile responsive design"],
                  cta: "Start for Free",
                  popular: false
                },
                { 
                  title: "Business Suite", 
                  description: "Comprehensive solution for growing businesses with expanding needs",
                  features: ["Unlimited products", "Advanced payment options", "Real-time inventory management", "Marketing tools integration", "Customer analytics"],
                  cta: "Get Business Suite",
                  popular: true
                },
                { 
                  title: "Enterprise Platform", 
                  description: "Custom-built solutions for large-scale operations with complex requirements",
                  features: ["Multi-store management", "Advanced B2B features", "Global shipping optimization", "Enterprise-grade security", "Dedicated support team"],
                  cta: "Contact Sales",
                  popular: false
                }
              ].map((platform, index) => (
                <Col lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className={`platform-card border-0 shadow h-100 ${platform.popular ? 'popular' : ''}`}>
                      {platform.popular && (
                        <div className="popular-badge">
                          <Badge bg="primary" className="px-3 py-2">Most Popular</Badge>
                        </div>
                      )}
                      <Card.Body className="p-4">
                        <h4 className="fw-bold mb-3">{platform.title}</h4>
                        <p className="text-muted mb-4">{platform.description}</p>
                        <ul className="feature-list mb-4">
                          {platform.features.map((feature, i) => (
                            <li key={i} className="mb-2">
                              <span className="feature-check me-2">✓</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <Button 
                          variant={platform.popular ? "primary" : "outline-primary"} 
                          className="w-100"
                        >
                          {platform.cta}
                        </Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Powerful Features</h2>
            <p className="lead text-muted">Everything you need to run and grow your online store</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { icon: <ShoppingCart size={40} />, title: "Intuitive Storefront", description: "Beautiful, customizable storefronts that convert visitors into customers" },
                { icon: <CreditCard size={40} />, title: "Secure Payments", description: "Multiple payment gateways with fraud protection and one-click checkout" },
                { icon: <Package size={40} />, title: "Inventory Management", description: "Real-time tracking across multiple locations and automated reordering" },
                { icon: <TrendingUp size={40} />, title: "Sales Analytics", description: "Detailed insights into sales performance, customer behavior, and trends" },
                { icon: <Truck size={40} />, title: "Shipping Integration", description: "Seamless connection with major carriers and automated fulfillment" },
                { icon: <ShieldCheck size={40} />, title: "Advanced Security", description: "PCI compliant platform with regular security updates and monitoring" },
                { icon: <Smartphone size={40} />, title: "Mobile Optimization", description: "Responsive design ensures perfect shopping experience on any device" },
                { icon: <Gift size={40} />, title: "Marketing Tools", description: "Built-in promotion management, discount codes, and loyalty programs" },
                { icon: <Settings size={40} />, title: "Customization Options", description: "Tailor your store's look and functionality to match your brand" }
              ].map((feature, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className="feature-card border-0 shadow-sm h-100 hover-lift">
                      <Card.Body className="p-4">
                        <div className="feature-icon mb-3 text-primary">
                          {feature.icon}
                        </div>
                        <h4 className="fw-bold mb-3">{feature.title}</h4>
                        <p className="text-muted mb-0">{feature.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Payment Integration Section */}
      <section className="payment-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-5 fw-bold mb-4">Seamless Payment Integration</h2>
                <p className="lead mb-4">Accept payments from customers worldwide with our secure and flexible payment processing solutions.</p>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Multiple Payment Options</h5>
                  <p className="text-muted">Support credit cards, digital wallets, bank transfers, buy-now-pay-later, and more to maximize conversion.</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Global Currency Support</h5>
                  <p className="text-muted">Sell internationally with automatic currency conversion and localized payment methods.</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Enhanced Security</h5>
                  <p className="text-muted">PCI DSS compliant platform with advanced fraud detection and prevention tools.</p>
                </div>
                
                <Button variant="primary" size="lg">Learn About Payment Options</Button>
              </motion.div>
              <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="payment-methods-container"
              >
                <div className="payment-methods-graphic p-4 bg-white shadow-lg rounded">
                  <div className="payment-methods-icons d-flex flex-wrap justify-content-center align-items-center gap-4 mb-4">
                    {/* This would typically contain payment provider logos */}
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="Visa" className="img-fluid" />
                    </div>
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="Mastercard" className="img-fluid" />
                    </div>
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="PayPal" className="img-fluid" />
                    </div>
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="Apple Pay" className="img-fluid" />
                    </div>
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="Google Pay" className="img-fluid" />
                    </div>
                    <div className="payment-icon shadow-sm">
                      <img src="/api/placeholder/80/40" alt="Stripe" className="img-fluid" />
                    </div>
                  </div>
                  <div className="payment-demo-screen">
                    <img 
                      src="/api/placeholder/500/300" 
                      alt="Payment Gateway Interface" 
                      className="img-fluid rounded shadow"
                    />
                  </div>
                </div>
              </motion.div>
            </Col>
          </Col>
          </Row>
        </Container>
      </section>

      {/* Inventory Management Section */}
      <section className="inventory-section py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="order-lg-2 mb-4 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-5 fw-bold mb-4">Smart Inventory Management</h2>
                <p className="lead mb-4">Keep track of your products across multiple channels and warehouses with our intelligent inventory system.</p>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Real-time Tracking</h5>
                  <p className="text-muted">Monitor stock levels in real-time across all sales channels and warehouse locations.</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Automated Reordering</h5>
                  <p className="text-muted">Set up automated purchase orders when inventory falls below specified thresholds.</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Barcode Scanning</h5>
                  <p className="text-muted">Streamline receiving and fulfillment processes with integrated barcode scanning.</p>
                </div>
                
                <Button variant="primary" size="lg">Explore Inventory Features</Button>
              </motion.div>
            </Col>
            <Col lg={6} className="order-lg-1">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="inventory-dashboard-container"
              >
                <div className="inventory-dashboard p-4 bg-white shadow-lg rounded">
                  <div className="inventory-dashboard-screen">
                    <img 
                      src="/api/placeholder/500/300" 
                      alt="Inventory Management Dashboard" 
                      className="img-fluid rounded shadow"
                    />
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our Clients Love Us</h2>
            <p className="lead text-muted">See what store owners have to say about our e-commerce solutions</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { 
                  name: "Sarah Johnson",
                  position: "Owner, Boutique Bliss",
                  image: "/api/placeholder/80/80",
                  quote: "Switching to this platform doubled our online sales in just three months. The inventory management system saved us countless hours of manual work."
                },
                { 
                  name: "Michael Rodriguez",
                  position: "CEO, TechGadgets",
                  image: "/api/placeholder/80/80",
                  quote: "The seamless integration between our physical stores and online platform has transformed our business. Customer satisfaction has never been higher."
                },
                { 
                  name: "Emma Davis",
                  position: "Founder, Organic Essentials",
                  image: "/api/placeholder/80/80",
                  quote: "The marketing tools built into the platform helped us grow our email list by 300% and dramatically increased our repeat customer rate."
                }
              ].map((testimonial, index) => (
                <Col lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className="testimonial-card border-0 shadow h-100">
                      <Card.Body className="p-4">
                        <div className="testimonial-quote mb-4">
                          <i className="quote-icon">❝</i>
                          <p className="mb-0">{testimonial.quote}</p>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="testimonial-avatar me-3">
                            <img 
                              src={testimonial.image} 
                              alt={testimonial.name} 
                              className="rounded-circle"
                              width="60"
                              height="60"
                            />
                          </div>
                          <div>
                            <h5 className="fw-bold mb-1">{testimonial.name}</h5>
                            <p className="text-muted mb-0">{testimonial.position}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Implementation Process */}
      <section className="implementation-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Simple Implementation Process</h2>
            <p className="lead text-muted">Get your online store up and running in just four steps</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="process-timeline"
              >
                {[
                  { 
                    step: 1, 
                    title: "Discovery & Planning",
                    description: "We'll work with you to understand your business needs and goals, and create a customized implementation plan."
                  },
                  { 
                    step: 2, 
                    title: "Design & Configuration",
                    description: "Our team will design your store and configure all settings, payment methods, shipping options, and more."
                  },
                  { 
                    step: 3, 
                    title: "Product Setup & Training",
                    description: "We'll help you set up your products, categories, and inventory, and train your team on using the platform."
                  },
                  { 
                    step: 4, 
                    title: "Launch & Optimization",
                    description: "After final checks, we'll launch your store and provide ongoing support to optimize your e-commerce performance."
                  }
                ].map((step, index) => (
                  <motion.div 
                    key={index}
                    variants={fadeIn}
                    className="process-step d-flex mb-4"
                  >
                    <div className="process-number me-4">
                      <span>{step.step}</span>
                    </div>
                    <div className="process-content">
                      <h4 className="fw-bold mb-2">{step.title}</h4>
                      <p className="text-muted mb-0">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <motion.section 
        className="cta-section text-white text-center py-5"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2 className="display-5 fw-bold mb-4">Ready to grow your online business?</h2>
              <p className="lead mb-4">Launch your e-commerce store today and start selling to customers worldwide.</p>
              <Button variant="light" size="lg" className="me-3 mb-3">Start Free Trial</Button>
              <Button variant="outline-light" size="lg" className="mb-3">Schedule Demo</Button>
            </Col>
          </Row>
        </Container>
      </motion.section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Frequently Asked Questions</h2>
            <p className="lead text-muted">Find answers to common questions about our e-commerce solutions</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
              >
                {[
                  { 
                    question: "How long does it take to set up an online store?", 
                    answer: "Basic stores can be set up in as little as 1-2 days. More complex stores with custom features may take 1-2 weeks. Our team works efficiently to get you selling as quickly as possible while ensuring everything works perfectly." 
                  },
                  { 
                    question: "Can I migrate from another e-commerce platform?", 
                    answer: "Yes, we offer seamless migration services from all major e-commerce platforms. We'll transfer your products, customer data, order history, and other important information to ensure a smooth transition with minimal disruption." 
                  },
                  { 
                    question: "What payment gateways are supported?", 
                    answer: "We support all major payment gateways including Stripe, PayPal, Square, Authorize.net, Amazon Pay, Apple Pay, Google Pay, and many more. We can also integrate with country-specific payment methods as needed." 
                  },
                  { 
                    question: "Is technical knowledge required to manage my store?", 
                    answer: "No technical knowledge is required. Our platform features an intuitive admin interface that makes it easy to manage products, orders, customers, and settings. We also provide comprehensive training and ongoing support." 
                  }
                ].map((faq, index) => (
                  <motion.div 
                    key={index}
                    variants={fadeIn}
                    className="mb-4"
                  >
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{faq.question}</h5>
                        <p className="text-muted mb-0">{faq.answer}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default EcommerceSolutions;